import { fetchPutResponse } from "../../../utilities/ApiUtilities";
import { NewsFeedRequestSerializer } from "./NewsFeedSerializer";

// Common function to handle the response and set the news list
const handleNewsResponse = (
  responseJson,
  setNewsList,
  setDocumentIds,
  setNoMoreNews,
  SetOffset,
  offset,
  newsList
) => {
  // An error body (422, 401, 5xx) has no `data`; treat it as no more news.
  const articles = Array.isArray(responseJson?.data) ? responseJson.data : [];
  if (articles.length > 0) {
    if (SetOffset) {
      SetOffset(offset + 100);
    }
    const relevantNews = articles
      .filter((news) => news.ner_relevance_score > 0.2)
      .sort(compareNews);
    const allNullScores = relevantNews.every(
      (article) => article.ner_relevance_score === null
    );

    // Check if all articles have scores less than 0.2
    const allLowScores = relevantNews.every(
      (article) =>
        article.ner_relevance_score === null ||
        article.ner_relevance_score <= 0.2
    );
    let finalNewsList;
    if (allNullScores) {
      finalNewsList = relevantNews; // Show all if all scores are null
    } else if (allLowScores) {
      finalNewsList = []; // Show none if all scores are less than 0.2
    } else {
      finalNewsList = relevantNews; // Show all other cases
    }

    if (newsList && newsList.length > 0) {
      setNewsList([...newsList, ...finalNewsList]);
    } else {
      setNewsList(finalNewsList);
    }

    const documentIds = finalNewsList.map((news) => ({ doc_id: news.id }));
    setDocumentIds(documentIds);
  } else {
    setDocumentIds([]);
    setNoMoreNews(true);
  }
};

// Common function to fetch news
const fetchNews = async (newsFeedRequest) => {
  try {
    const response = await fetchPutResponse("news/fetch_news", newsFeedRequest);
    return await response.json();
  } catch (error) {
    console.error("Failed to fetch news:", error);
    return null;
  }
};

const compareNews = (a, b) => {
  const dateA = new Date(a.data_timestamp);
  const dateB = new Date(b.data_timestamp);

  // Compare data_timestamp first, then relevance_score if timestamps are equal
  if (dateA !== dateB) return dateB - dateA;
  return b.relevance_score - a.relevance_score;
};

export const loadNews = async (
  dashboardChartData,
  setNewsList,
  setIsNewsLoading,
  filterDate,
  setDocumentIds,
  SetOffset,
  setNoMoreNews,
  isinRef
) => {
  const offset = 0;
  const isin = dashboardChartData.map((x) => x.isin).filter(Boolean);
  if (isin.length === 0) return;

  const newsFeedRequest = new NewsFeedRequestSerializer({
    isin,
    offset,
    filterDate,
  });

  SetOffset(offset);
  setIsNewsLoading(true);
  setNewsList([]);
  setNoMoreNews(false);

  const responseJson = await fetchNews(newsFeedRequest);
  // A newer selection owns the feed now.
  if (isinRef.current !== dashboardChartData) return;
  if (responseJson) {
    handleNewsResponse(
      responseJson,
      setNewsList,
      setDocumentIds,
      setNoMoreNews,
      SetOffset,
      offset
    );
  }
  setIsNewsLoading(false);
};

export const handleNewsClick = async (
  setSelectedNews,
  news,
  document_ids,
  SetPrevDocumentIds,
  setDocumentIds
) => {
  try {
    SetPrevDocumentIds(document_ids);
    setSelectedNews(news);
    setDocumentIds([{ doc_id: news.id }]);
  } catch (error) {
    console.error("Failed to handle news click:", error);
  }
};

export const loadMoreNews = async ({
  dashboardChartData,
  offset,
  setIsMoreNewsLoading,
  setNewsList,
  SetOffset,
  filterDate,
  setNoMoreNews,
  setDocumentIds,
  documentIds,
  newsList,
}) => {
  if (offset === 0) return;

  setIsMoreNewsLoading(true);
  const isin = dashboardChartData.map((x) => x.isin).filter(Boolean);
  const limit = 100;

  const newsFeedRequest = new NewsFeedRequestSerializer({
    isin,
    offset,
    filterDate,
  });
  const responseJson = await fetchNews(newsFeedRequest);
  if (responseJson) {
    handleNewsResponse(
      responseJson,
      setNewsList,
      setDocumentIds,
      setNoMoreNews,
      SetOffset,
      offset,
      newsList
    );
  }
  setIsMoreNewsLoading(false);
};

export const loadNewsPointClick = async (
  isin,
  setNewsList,
  setIsNewsLoading,
  startDate,
  setDocumentIds,
  setNoMoreNews,
  granularity,
  pointClickNewsLookbackDays
) => {
  setIsNewsLoading(true);
  setNewsList([]);

  const date = new Date(startDate || new Date());
  let start_date = new Date(date);
  let end_date = new Date(date);

  if (
    Number.isFinite(pointClickNewsLookbackDays) &&
    pointClickNewsLookbackDays >= 0
  ) {
    start_date.setDate(start_date.getDate() - pointClickNewsLookbackDays);
  } else if (granularity === "WEEKLY") {
    start_date.setDate(start_date.getDate() - 3);
    end_date.setDate(end_date.getDate() + 3);
  }

  const newsFeedRequest = new NewsFeedRequestSerializer({
    isin,
    offset: 0,
    filterDate: { startDate: start_date, endDate: end_date },
  });

  const responseJson = await fetchNews(newsFeedRequest);
  if (responseJson) {
    handleNewsResponse(
      responseJson,
      setNewsList,
      setDocumentIds,
      setNoMoreNews
    );
    setNoMoreNews(true);
  }

  setIsNewsLoading(false);
};
